<template>
    <v-container fluid>
        <v-card elevation="3" tile>
            <v-card-title class="pa-2 pl-4">
                <v-icon left color="primary">mdi-billboard</v-icon>
                Werbeschildverwaltung
            </v-card-title>
            <v-divider/>

            <v-card-text>
                <v-row>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="search"
                            dense
                            outlined
                            clearable
                            prepend-inner-icon="mdi-magnify"
                            hint="Werbeschild suchen"
                            persistent-hint
                        />
                    </v-col>
                </v-row>

                <v-data-table
                    :items="signs"
                    :headers="headers"
                    :search="search"
                    :loading="loading"
                    sort-by="iid"
                >
                    <template v-slot:item.postalCode="{ item }">
                        {{ item.postalCode || '-' }}
                    </template>

                    <template v-slot:item.linkedSigns="{ item }">
                        <div v-if="item.linkedSigns && item.linkedSigns.length > 0" class="d-flex flex-wrap ga-1">
                            <v-chip
                                v-for="linkedSign in item.linkedSigns"
                                :key="linkedSign.id"
                                x-small
                                outlined
                                color="primary"
                                class="mr-1 mb-1"
                            >
                                {{ formatSignLabel(linkedSign) }}
                            </v-chip>
                        </div>
                        <span v-else>-</span>
                    </template>

                    <template v-slot:item.picture="{ item }">
                        <v-menu
                            v-if="item.picture && item.picture.id"
                            open-on-hover
                            offset-y
                            bottom
                            nudge-right="12"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-avatar
                                    size="42"
                                    tile
                                    class="sign-preview-activator"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-img :src="apiAttachmentUrl(item.picture)"/>
                                </v-avatar>
                            </template>
                            <v-card class="pa-2">
                                <v-img
                                    :src="apiAttachmentUrl(item.picture)"
                                    max-width="320"
                                    max-height="320"
                                    contain
                                />
                            </v-card>
                        </v-menu>
                        <span v-else>-</span>
                    </template>

                    <template v-slot:item.fee="{ item }">
                        {{ formatMoney(item.fee) }}
                    </template>

                    <template v-slot:item.actions="{ item }">
                        <v-btn x-small color="primary" class="mr-2" @click="openEdit(item)">
                            <v-icon left small>mdi-pencil</v-icon>
                            Bearbeiten
                        </v-btn>
                        <v-btn x-small color="error" @click="remove(item)">
                            <v-icon left small>mdi-delete</v-icon>
                            Löschen
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card-text>

            <v-divider/>
            <v-card-actions>
                <v-btn small color="primary" @click="openCreate()">
                    <v-icon left>mdi-plus</v-icon>
                    Neues Werbeschild
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog v-model="editDialog" max-width="700" persistent>
            <v-card>
                <v-card-title class="pb-2 pt-2">
                    <v-icon left>mdi-billboard</v-icon>
                    {{ editModel.id ? 'Werbeschild bearbeiten' : 'Werbeschild erstellen' }}
                </v-card-title>
                <v-divider/>

                <v-card-text class="pt-4">
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model.number="editModel.iid"
                                type="number"
                                min="0"
                                step="1"
                                dense
                                outlined
                                hint="Schild-ID"
                                persistent-hint
                                prepend-inner-icon="mdi-identifier"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="editModel.postalCode"
                                dense
                                outlined
                                hint="PLZ/Straße"
                                persistent-hint
                                prepend-inner-icon="mdi-map-marker"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model.number="editModel.fee"
                                type="number"
                                min="0"
                                step="1"
                                dense
                                outlined
                                hint="Gebühr"
                                persistent-hint
                                prepend-inner-icon="mdi-currency-eur"
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" md="8">
                            <v-file-input
                                accept="image/*"
                                dense
                                outlined
                                show-size
                                hint="Bild auswählen"
                                persistent-hint
                                prepend-inner-icon="mdi-image"
                                @change="uploadPictureFromFile"
                                :loading="uploadingPicture"
                                :disabled="uploadingPicture"
                            />
                            <v-text-field
                                v-model="pictureClipboardInput"
                                dense
                                outlined
                                clearable
                                clear-icon="mdi-delete"
                                prepend-inner-icon="mdi-clipboard-outline"
                                hint="Bild direkt aus der Zwischenablage einfügen"
                                persistent-hint
                                placeholder="Hier STRG+V drücken"
                                :readonly="uploadingPicture"
                                @paste.prevent="onPicturePaste"
                                @click:clear="pictureClipboardInput = null"
                                class="mt-2"
                            />
                            <v-autocomplete
                                v-model="editModel.linkedSigns"
                                :items="availableLinkedSigns"
                                :item-text="formatSignLabel"
                                return-object
                                multiple
                                chips
                                deletable-chips
                                dense
                                outlined
                                hint="Verknüpfte Schilder an derselben Adresse"
                                persistent-hint
                                prepend-inner-icon="mdi-link-variant"
                                class="mt-2"
                            />
                        </v-col>
                        <v-col cols="12" md="4" class="d-flex align-center justify-center">
                            <v-avatar size="96" tile>
                                <v-img :src="picturePreviewUrl"/>
                            </v-avatar>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider/>
                <v-card-actions>
                    <v-btn small color="success" :loading="saving" @click="save()" :disabled="uploadingPicture">
                        <v-icon left>mdi-content-save</v-icon>
                        Speichern
                    </v-btn>
                    <v-spacer/>
                    <v-btn small color="grey" @click="closeEdit()" :disabled="saving || uploadingPicture">
                        Abbrechen
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<style scoped>
.sign-preview-activator {
    cursor: zoom-in;
}
</style>

<script>
import Swal from 'sweetalert2';

export default {
    data: () => ({
        loading: false,
        saving: false,
        uploadingPicture: false,
        pictureClipboardInput: null,
        search: '',
        signs: [],
        editDialog: false,
        editModel: {
            id: null,
            iid: 0,
            postalCode: '',
            fee: 0,
            picture: null,
            linkedSigns: [],
        },
        headers: [
            {text: 'Schild-ID', value: 'iid'},
            {text: 'PLZ/Straße', value: 'postalCode'},
            {text: 'Verknüpfte Schilder', value: 'linkedSigns', sortable: false},
            {text: 'Bild', value: 'picture', sortable: false},
            {text: 'Gebühr', value: 'fee'},
            {text: 'Aktionen', value: 'actions', sortable: false},
        ],
    }),
    computed: {
        availableLinkedSigns() {
            const currentId = this.editModel?.id || null;
            return this.signs.filter(sign => sign.id !== currentId);
        },
        nextFreeSignIid() {
            const iids = this.signs
                .map(sign => Number(sign?.iid))
                .filter(iid => Number.isInteger(iid) && iid >= 0);

            return iids.length > 0 ? Math.max(...iids) + 1 : 0;
        },
        picturePreviewUrl() {
            if (this.editModel.picture && this.editModel.picture.id) {
                return this.apiAttachmentUrl(this.editModel.picture);
            }
            return require('../../../assets/company.png');
        }
    },
    methods: {
        normalizeLinkedSigns(linkedSigns) {
            const source = Array.isArray(linkedSigns) ? linkedSigns : [];
            const dedupe = new Map();

            for (const linkedSign of source) {
                if (!linkedSign?.id) continue;
                dedupe.set(linkedSign.id, {
                    id: linkedSign.id,
                    iid: Number(linkedSign.iid) || 0,
                    postalCode: linkedSign.postalCode || '',
                    fee: Math.max(0, Number(linkedSign.fee) || 0),
                    picture: linkedSign.picture || null,
                });
            }

            return Array.from(dedupe.values()).sort((a, b) => (a.iid || 0) - (b.iid || 0));
        },
        normalizeSign(sign) {
            return {
                id: sign?.id || null,
                iid: Number(sign?.iid) || 0,
                postalCode: sign?.postalCode || '',
                fee: Math.max(0, Number(sign?.fee) || 0),
                picture: sign?.picture || null,
                linkedSigns: this.normalizeLinkedSigns(sign?.linkedSigns),
            };
        },
        formatSignLabel(sign) {
            if (!sign) return '-';
            const parts = [`#${sign.iid ?? '-'}`];
            if (sign.postalCode) {
                parts.push(`PLZ ${sign.postalCode}`);
            }
            return parts.join(' - ');
        },
        formatMoney(value) {
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
            }).format(Number(value) || 0);
        },
        fetchSigns() {
            this.loading = true;
            return this.apiGetCompanySigns().then(r => {
                const source = Array.isArray(r.data) ? r.data : [];
                this.signs = source.map(sign => this.normalizeSign(sign));
            }).catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Werbeschilder konnten nicht geladen werden',
                    text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                });
            }).finally(() => {
                this.loading = false;
            });
        },
        openCreate() {
            this.editModel = {
                id: null,
                iid: this.nextFreeSignIid,
                postalCode: '',
                fee: 0,
                picture: null,
                linkedSigns: [],
            };
            this.editDialog = true;
        },
        openEdit(item) {
            this.editModel = this.normalizeSign(item);
            this.editDialog = true;
        },
        closeEdit() {
            this.editDialog = false;
            this.uploadingPicture = false;
            this.pictureClipboardInput = null;
            this.editModel = {
                id: null,
                iid: 0,
                postalCode: '',
                fee: 0,
                picture: null,
                linkedSigns: [],
            };
        },
        async uploadPictureFromBase64(image, filename = 'sign.png') {
            this.uploadingPicture = true;
            try {
                const response = await this.apiUploadEvidence(image, filename, 'Werbeschild', true);
                this.editModel.picture = {id: response?.data?.id};
            } catch (err) {
                Swal.fire({
                    icon: 'error',
                    title: 'Bild konnte nicht hochgeladen werden',
                    text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                });
            } finally {
                this.uploadingPicture = false;
                this.pictureClipboardInput = null;
            }
        },
        async uploadPictureFromFile(file) {
            if (!file) return;

            const image = await this.toBase64(file);
            return this.uploadPictureFromBase64(image, file.name || 'sign.png');
        },
        onPicturePaste(event) {
            const item = event.clipboardData?.items?.[0];
            if (!item) return;

            if (item.kind === 'file') {
                const file = item.getAsFile();
                this.toBase64(file).then(r => {
                    this.uploadPictureFromBase64(r, file?.name || 'sign.png');
                });
            } else {
                Swal.fire({
                    icon: 'info',
                    title: 'Kein Bild in der Zwischenablage',
                    text: 'Bitte ein Bild kopieren und erneut einfügen.',
                });
            }
        },
        save() {
            const postalCode = `${this.editModel.postalCode || ''}`.trim();

            if (!postalCode) {
                Swal.fire({icon: 'warning', title: 'PLZ/Straße fehlt', text: 'Bitte eine PLZ oder Straße angeben.'});
                return;
            }

            this.saving = true;
            const payload = {
                iid: Math.max(0, Number(this.editModel.iid) || 0),
                postalCode,
                fee: Math.max(0, Number(this.editModel.fee) || 0),
                picture: this.editModel.picture && this.editModel.picture.id ? this.editModel.picture : null,
                linkedSigns: this.normalizeLinkedSigns(this.editModel.linkedSigns).map(sign => ({id: sign.id})),
            };

            const req = this.editModel.id
                ? this.apiUpdateCompanySign(this.editModel.id, payload)
                : this.apiCreateCompanySign(payload);

            req.then(() => {
                this.closeEdit();
                return this.fetchSigns();
            }).catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Werbeschild konnte nicht gespeichert werden',
                    text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                });
            }).finally(() => {
                this.saving = false;
            });
        },
        remove(item) {
            Swal.fire({
                icon: 'warning',
                title: 'Werbeschild löschen?',
                text: `Soll Werbeschild #${item?.iid ?? '-'} wirklich gelöscht werden?`,
                showCancelButton: true,
                cancelButtonText: 'Abbrechen',
                confirmButtonText: 'Löschen',
            }).then(r => {
                if (!r.isConfirmed) return;

                this.loading = true;
                this.apiDeleteCompanySign(item.id)
                    .then(() => this.fetchSigns())
                    .catch(err => {
                        Swal.fire({
                            icon: 'error',
                            title: 'Werbeschild konnte nicht gelöscht werden',
                            text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                        });
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            });
        },
    },
    mounted() {
        this.fetchSigns();
    },
};
</script>
